import * as React from 'react'
// libs
import { v4 as uuid } from 'uuid'
// components
import Grid from '@mui/material/Grid2'
import { ReadMore } from 'components/UI'
import {
  ReduxPicker,
  ReduxInput,
  ReduxSelect,
  ReduxSwitch,
  ReduxWysiwyg,
  TruncatedInput,
  CreatableSelect,
  MultiValueLabel,
  ReduxEntitySelect
} from 'components/FormElements'
import OpenRiskControl from 'components/FormElements/components/OpenRiskLink/Control'
import {
  InputMethod,
  RecurrenceText,
  ResultInputType,
  ResultTargetText
} from 'components/Modals/PlanItems/KPI/MinimizedModal/components/DetailsSection/components/DetailsForm/components/Fields/index'
import ParentRecord from 'components/Modals/PlanItems/components/ParentRecordField'
import ConditionOption from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/ConditionOption'
import DocumentOrLink from 'components/FormElements/DocumentOrLink/Strategy'
import ReduxNumberInput from 'components/FormElements/ReduxForm/ReduxNumberInput'
import ReduxLocationPicker from 'components/FormElements/ReduxForm/ReduxLocationPicker'
import PlannedCompletionDate from 'components/Modals/PlanItems/Action/MinimizedModal/components/DetailsSection/components/DetailsForm/PlannedCompletionDate'
import ReduxResponseTypeSelect from 'components/FormElements/ReduxForm/ReduxResponseTypesSelect'
// constants
import { ENTITIES, SELECT_GROUP_LABELS } from 'constants/index'
// icons
import {
  DateIcon,
  JsonIcon,
  LookupIcon,
  NumberIcon,
  PicklistIcon,
  SwitchIcon,
  TagIcon,
  TextIcon,
  TableIcon,
  MatrixIcon,
  LabelIcon,
  SectionIcon,
  LocationIcon,
  AttachmentIcon,
  FormIcon,
  LinkedFormIcon,
  RecordLinksIcon,
  ParentFieldsIcon
} from 'themes/views/objects'
// graphql
import {
  BUSINESS_ACTIVITIES_SELECT_QUERY,
  BUSINESS_PROCESSES_SELECT_QUERY,
  BUSINESS_UNITS_PUBLIC_SELECT_QUERY,
  BUSINESS_UNITS_SELECT_QUERY,
  ENTITY_SELECT_QUERY,
  LOCATIONS_PUBLIC_SELECT_QUERY,
  LOCATIONS_SELECT_QUERY,
  PLAN_ACTIONS_SELECT_QUERY,
  PLAN_KPIS_SELECT_QUERY,
  PLAN_OBJECTIVES_SELECT_QUERY,
  PLAN_SELECT_QUERY,
  PLAN_TYPES_SELECT_QUERY,
  POSITIONS_PUBLIC_SELECT_QUERY,
  POSITIONS_SELECT_QUERY,
  RISK_SELECT_PUBLIC_QUERY,
  RISK_SELECT_QUERY,
  USERS_PUBLIC_SELECT_QUERY,
  USERS_SELECT_QUERY
} from 'graphql/objects/queries'
// types
import { IOption } from 'components/FormElements/Select'
import type { FormatOptionLabelMeta } from 'react-select'
import { FieldSource, FieldType, IField, IFieldOption, IFieldValueMapItem, IObjectEntity } from 'types/graphql'
// utils
import { required } from 'utils/validators'
import { safeGetOr } from '../fp'
import { getValueFromOption } from 'utils/form'
import { castArray, flatten, get, isEmpty, isNil, merge, uniq } from 'lodash'
import { find, get as getFp, getOr, pipe, sortBy } from 'lodash/fp'

export const getIconForProperty = (property: string): React.ReactNode => {
  switch (property) {
    case FieldType.Switch: {
      return <SwitchIcon />
    }
    case FieldType.Date: {
      return <DateIcon />
    }
    case FieldType.PickList: {
      return <PicklistIcon />
    }
    case FieldType.Text: {
      return <TextIcon />
    }
    case FieldType.Number: {
      return <NumberIcon />
    }
    case FieldType.Lookup: {
      return <LookupIcon />
    }
    case FieldType.Tag: {
      return <TagIcon />
    }
    case FieldType.Json: {
      return <JsonIcon />
    }
    case FieldType.Table: {
      return <TableIcon />
    }
    case FieldType.RiskMatrix: {
      return <MatrixIcon />
    }
    case FieldType.Label: {
      return <LabelIcon />
    }
    case FieldType.DocumentOrLink: {
      return <AttachmentIcon />
    }
    case FieldType.Section: {
      return <SectionIcon />
    }
    case FieldType.Address: {
      return <LocationIcon />
    }
    case FieldType.Form: {
      return <FormIcon />
    }
    case FieldType.LinkedForm: {
      return <LinkedFormIcon />
    }
    case FieldType.RecordLinks: {
      return <RecordLinksIcon />
    }
    case FieldType.ParentRelatedForms: {
      return <ParentFieldsIcon />
    }
  }
}

export const getOptionSource = (sources: FieldSource[]) => {
  if (sources.includes(FieldSource.Users)) {
    return ENTITIES.USER
  }

  if (sources.includes(FieldSource.Positions)) {
    return ENTITIES.POSITION
  }
}

export const getEntitySelectOptions =
  (field?: IField) =>
  ({ data }: Record<string, any>) => {
    if (isEmpty(data)) {
      return []
    }

    const options = Object.entries(data)
      .map(([key, value]) => ({ [key]: value }))
      .map((item) => {
        let options

        for (const property in item) {
          if (property === FieldSource.Plans) {
            options = {
              label: SELECT_GROUP_LABELS.PLAN,
              options: get(item, 'PLANS.data', [] as any).map(({ id, name }) => ({
                label: name,
                value: id,
                entity: ENTITIES.PLAN
              }))
            }
          }

          if (property === FieldSource.PlanTypes) {
            options = {
              label: SELECT_GROUP_LABELS.PLAN_TYPES,
              options: get(item, 'PLAN_TYPES.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.PLAN_TYPE
              }))
            }
          }

          if (property === FieldSource.Actions) {
            options = {
              label: SELECT_GROUP_LABELS.ACTION,
              options: get(item, 'ACTIONS.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.PLAN_ACTION
              }))
            }
          }

          if (property === FieldSource.Objectives) {
            options = {
              label: SELECT_GROUP_LABELS.OBJECTIVE,
              options: get(item, 'OBJECTIVES.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.PLAN_OBJECTIVE
              }))
            }
          }

          if (property === FieldSource.Kpis) {
            options = {
              label: SELECT_GROUP_LABELS.KPI,
              options: get(item, 'KPIS.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.PLAN_KPI
              }))
            }
          }

          if (property === FieldSource.Users) {
            options = {
              label: SELECT_GROUP_LABELS.PEOPLE,
              options: get(item, 'USERS.data', [] as any).map(
                ({ id, firstName, lastName, businessUnits, displayName, email }) => ({
                  label: [
                    displayName || `${firstName} ${lastName}`,
                    businessUnits?.length ? `(${businessUnits.map(({ description }) => description).join(', ')})` : '',
                    field?.displayEmail ? ` - ${email}` : ''
                  ]
                    .filter(Boolean)
                    .join(' '),
                  value: id,
                  entity: ENTITIES.USER,
                  ...(get(field, 'objectSecurityId') && { objectSecurityId: field.objectSecurityId })
                })
              )
            }
          }

          if (property === FieldSource.Positions) {
            options = {
              label: SELECT_GROUP_LABELS.POSITIONS,
              options: get(item, 'POSITIONS.data', [] as any).map(({ id, description, user }) => ({
                label: `${description} ${user?.businessUnits?.length ? `(${user.businessUnits.map((item) => item.description).join(', ')})` : ''}`,
                value: id,
                entity: ENTITIES.POSITION,
                ...(get(field, 'objectSecurityId') && { objectSecurityId: field.objectSecurityId })
              }))
            }
          }

          if (property === FieldSource.BusinessActivities) {
            options = {
              label: SELECT_GROUP_LABELS.BUSINESS_ACTIVITIES,
              options: get(item, 'BUSINESS_ACTIVITIES.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.BUSINESS_ACTIVITY
              }))
            }
          }

          if (property === FieldSource.BusinessProcesses) {
            options = {
              label: SELECT_GROUP_LABELS.BUSINESS_PROCESSES,
              options: get(item, 'BUSINESS_PROCESSES.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.BUSINESS_PROCESS
              }))
            }
          }

          if (property === FieldSource.BusinessUnits) {
            options = {
              label: SELECT_GROUP_LABELS.BUSINESS_UNITS,
              options: get(item, 'BUSINESS_UNITS.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.BUSINESS_UNIT
              }))
            }
          }

          if (property === FieldSource.Locations) {
            options = {
              label: SELECT_GROUP_LABELS.LOCATIONS,
              options: get(item, 'LOCATIONS.data', [] as any).map(({ id, description }) => ({
                label: description,
                value: id,
                entity: ENTITIES.LOCATION
              }))
            }
          }

          if (property === FieldSource.Risks) {
            options = {
              label: SELECT_GROUP_LABELS.ANOTHER_FORM,
              options: (get(item, 'RISKS') || ([] as any)).map(({ id, label, riskTemplateId }) => ({
                label,
                value: id,
                riskTemplateId,
                entity: ENTITIES.RISK
              }))
            }
          }
        }
        return options
      })

    return flatten(options)
  }

export const getEntitySelectQuery = (sources: string[], isPublic?: boolean) => {
  const data = { queries: [], variables: [] }

  sources.forEach((source) => {
    if (source === FieldSource.Plans) {
      data.queries.push(PLAN_SELECT_QUERY(source))
      data.variables.push(`$planFilter: PlanFilterInput`)
      return
    }

    if (source === FieldSource.PlanTypes) {
      data.queries.push(PLAN_TYPES_SELECT_QUERY(source))
      data.variables.push(`$planTypeFilter: PlanTypeFilterInput`)
      return
    }

    if (source === FieldSource.Actions) {
      data.queries.push(PLAN_ACTIONS_SELECT_QUERY(source))
      data.variables.push(`$actionFilter: PlanActionFilterInput`)
      return
    }

    if (source === FieldSource.Objectives) {
      data.queries.push(PLAN_OBJECTIVES_SELECT_QUERY(source))
      data.variables.push(`$objectiveFilter: PlanObjectiveFilterInput`)
      return
    }

    if (source === FieldSource.Kpis) {
      data.queries.push(PLAN_KPIS_SELECT_QUERY(source))
      data.variables.push(`$kpiFilter: PlanKPIFilterInput`)
      return
    }

    if (source === FieldSource.Users) {
      data.queries.push((isPublic ? USERS_PUBLIC_SELECT_QUERY : USERS_SELECT_QUERY)(FieldSource.Users))
      data.variables.push(`$userFilter: UserFilter`)
      return
    }

    if (source === FieldSource.Positions) {
      data.queries.push((isPublic ? POSITIONS_PUBLIC_SELECT_QUERY : POSITIONS_SELECT_QUERY)(source))
      data.variables.push(`$positionFilter: PositionFilterInput`)
      return
    }

    if (source === FieldSource.BusinessActivities) {
      data.queries.push(BUSINESS_ACTIVITIES_SELECT_QUERY(FieldSource.BusinessActivities))
      data.variables.push(`$businessActivityFilter: BusinessActivityFilterInput`)
      return
    }

    if (source === FieldSource.BusinessProcesses) {
      data.queries.push(BUSINESS_PROCESSES_SELECT_QUERY(source))
      data.variables.push(`$businessProcessesFilter: BusinessProcessFilterInput`)
      return
    }

    if (source === FieldSource.BusinessUnits) {
      data.queries.push((isPublic ? BUSINESS_UNITS_PUBLIC_SELECT_QUERY : BUSINESS_UNITS_SELECT_QUERY)(source))
      data.variables.push(`$businessUnitFilter: BusinessUnitFilterInput`)
      return
    }

    if (source === FieldSource.Locations) {
      data.queries.push((isPublic ? LOCATIONS_PUBLIC_SELECT_QUERY : LOCATIONS_SELECT_QUERY)(source))
      data.variables.push(`$locationsFilter: LocationFilterInput`)
      return
    }

    if (source === FieldSource.Risks) {
      data.queries.push((isPublic ? RISK_SELECT_PUBLIC_QUERY : RISK_SELECT_QUERY)(source))
      data.variables.push(`$risksFilter: [ID!]!`)
      return
    }
  })

  // RISKS is the only source without pagination — declaring an unused variable would fail validation
  if (data.queries.some((query) => query.includes('pagination: $pagination'))) {
    data.variables.push(`$pagination: PaginationInput`)
  }

  if (isPublic) {
    data.variables.push(`$domain: String!`)
  }

  if (isEmpty(data.queries)) return

  return ENTITY_SELECT_QUERY(data)
}

type Filter = {
  [key: string]: boolean | string | string[]
}

type Pagination = {
  pageTake: number
  pageSkip: number
}

type Params = {
  pagination?: Pagination
  planFilter?: Filter
  planTypeFilter?: Filter
  actionFilter?: Filter
  objectiveFilter?: Filter
  kpiFilter?: Filter
  userFilter?: Filter
  positionFilter?: Filter
  businessActivityFilter?: Filter
  businessProcessesFilter?: Filter
  businessUnitFilter?: Filter
  locationsFilter?: Filter
  risksFilter?: string[]
  domain?: string
}

export const getEntitySelectVariables = ({
  ids,
  active = true,
  search,
  domain,
  sources,
  limitResponse,
  searchTemplateIds
}: {
  ids?: string[]
  active?: boolean
  search?: string
  domain?: string
  sources: string[]
  limitResponse?: any
  searchTemplateIds?: string[]
}) => {
  const params: Params = {}
  // the same record can be selected in several fields, and duplicates would inflate the page size
  const uniqIds = ids && uniq(ids)
  const value = uniqIds || search

  // an explicit list of ids must never be truncated by the page size — ask for exactly as many as requested
  params.pagination = { pageSkip: 0, pageTake: uniqIds?.length || 20 }

  sources.forEach((source) => {
    switch (source) {
      case FieldSource.Plans:
        params.planFilter = {
          ...(value && { [ids ? 'ids' : 'name']: value })
        }
        break
      case FieldSource.PlanTypes:
        params.planTypeFilter = {
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Actions:
        params.actionFilter = {
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Objectives:
        params.objectiveFilter = {
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Kpis:
        params.kpiFilter = {
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Users:
        params.userFilter = {
          ...(limitResponse && limitResponse),
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'search']: value })
        }
        break
      case FieldSource.Positions:
        params.positionFilter = {
          ...(limitResponse && limitResponse),
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.BusinessActivities:
        params.businessActivityFilter = {
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.BusinessProcesses:
        params.businessProcessesFilter = {
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.BusinessUnits:
        params.businessUnitFilter = {
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Locations:
        params.locationsFilter = {
          ...(active && { active: true }),
          ...(value && { [ids ? 'ids' : 'description']: value })
        }
        break
      case FieldSource.Risks:
        params.risksFilter = searchTemplateIds || uniqIds
        break
    }
  })

  if (domain) {
    params.domain = domain
  }

  return params
}

export const getFieldsList = (data: IObjectEntity[] = [], source: string): IField[] => {
  return pipe(
    find((objectEntity: IObjectEntity) => objectEntity.name.toLowerCase() === source.toLowerCase()),
    safeGetOr<any, any, any>([], 'fields'),
    sortBy((field: IField) => field.position)
  )(data)
}

export const formatOptionLabel = (option: IOption, meta: FormatOptionLabelMeta<IOption>) => {
  let formattedLabel = option.label

  if (meta.context === 'menu') {
    return formattedLabel
  }

  const emailSeparatorIndex = option.label?.indexOf(' - ')

  if (emailSeparatorIndex !== -1) {
    formattedLabel = option.label?.substring(0, emailSeparatorIndex).trim()
  }

  return formattedLabel
}

export const generateFields = ({
  field,
  form,
  callback,
  canModify,
  showInactive,
  shouldValidate = true,
  canSetPermissions,
  canViewRestricted
}: {
  field: IField
  form?: string
  callback?: () => void
  canModify?: boolean
  showInactive?: boolean
  shouldValidate?: boolean
  canSetPermissions?: boolean
  canViewRestricted?: boolean
}): React.ReactNode => {
  const validate = field.required && shouldValidate ? required : undefined

  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      return e.preventDefault()
    }
  }

  if (!showInactive && (!field.active || (field.restricted && !canViewRestricted))) {
    return
  }

  if (field.fieldType === FieldType.Switch) {
    return (
      <Grid key={field.id} className='switch-wrapper'>
        <ReduxSwitch
          name={field.name}
          label={field.label}
          validate={validate}
          disabled={!canModify}
          required={field.required}
        />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Date) {
    if (field.name === 'completionAt' && field.standard) {
      return <PlannedCompletionDate key={field.id} label={field.label} canModify={canModify} />
    }

    return (
      <Grid key={field.id} data-testid='risk-field-value' data-entity-id={field.id}>
        <ReduxPicker form={form} field={field} validate={validate} disabled={!canModify} required={field.required} />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.PickList) {
    let options: IFieldOption[] = field.options
      .filter(({ active, hidden }: any) => active && !hidden)
      .map(({ id, label }) => ({ value: id, label }))

    if (field.multiple) {
      options = options.filter((option) => option.value)
    }

    if (field.name === 'inputMethod' && field.standard) {
      return (
        <InputMethod
          form={form}
          key={field.id}
          options={options}
          label={field.label}
          canModify={canModify}
          fieldOptions={field.options}
        />
      )
    }

    if (field.name === 'resultInputType' && field.standard) {
      return (
        <ResultInputType
          form={form}
          key={field.id}
          options={options}
          label={field.label}
          canModify={canModify}
          fieldOptions={field.options}
        />
      )
    }

    if (field.responseType) {
      return (
        <ReduxResponseTypeSelect
          field={field}
          name={field.name}
          label={field.label}
          disabled={!canModify}
          required={field.required}
        />
      )
    }

    return (
      <Grid key={field.id} data-testid='risk-field-value' data-entity-id={field.id}>
        <ReduxSelect
          name={field.name}
          label={field.label}
          validate={validate}
          isDisabled={!canModify}
          required={field.required}
          isMultiple={field.multiple}
          isClearable={!field.multiple && !field.required}
          options={options as IOption[]}
          components={{ Option: ConditionOption }}
        />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Json) {
    if (field.name === 'recurrence') {
      return <RecurrenceText canModify={canModify} key={field.id} label={field.label} />
    }

    if (field.name === 'resultTarget') {
      return <ResultTargetText canModify={canModify} key={field.id} label={field.label} />
    }
  }

  if (field.fieldType === FieldType.Text) {
    if (field.wysiwyg) {
      return (
        <Grid key={field.id}>
          <ReduxWysiwyg name={field.name} label={field.label} validate={validate} />
        </Grid>
      )
    }

    if (field.multiline) {
      return (
        <Grid key={field.id}>
          <ReadMore
            form={form}
            fieldName={field.name}
            shrunkContent={({ truncated }) => <TruncatedInput label={field.label} truncated={truncated} />}
            expandedContent={({ recalculate, inputRef }) => (
              <ReduxInput
                multiline
                name={field.name}
                inputRef={inputRef}
                label={field.label}
                validate={validate}
                disabled={!canModify}
                handleOnBlur={recalculate}
              />
            )}
          />
        </Grid>
      )
    }

    return (
      <Grid key={field.id} onKeyPress={onKeyPress}>
        <ReduxInput
          name={field.name}
          label={field.label}
          validate={validate}
          disabled={!canModify}
          required={field.required}
        />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Number) {
    return (
      <Grid key={field.id} onKeyPress={onKeyPress}>
        <ReduxNumberInput field={field} disabled={!canModify} validate={validate} required={field.required} />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Lookup) {
    if (field.name === 'plan') {
      return <ParentRecord key={field.id} field={field} form={form} />
    }

    const isPlanTypeSelect = field.name === 'planType'

    const searchTemplateIds = field.searchTemplateIds ? [field.id] : null
    const sources = searchTemplateIds ? [...field.source, FieldSource.Risks] : field.source

    const query = getEntitySelectQuery(sources)
    const variables = getEntitySelectVariables({ sources, searchTemplateIds })

    const handleSearch = ({ search, refetch, variables }) => {
      refetch(merge({}, variables, getEntitySelectVariables({ sources, search, searchTemplateIds })))
    }

    return (
      <Grid key={field.id}>
        <ReduxEntitySelect
          isGrouped
          query={query}
          field={field}
          name={field.name}
          label={field.label}
          source={sources}
          validate={validate}
          variables={variables}
          isDisabled={!canModify}
          required={field.required}
          handleSearch={handleSearch}
          isMultiple={field.multiple}
          formatOptionLabel={formatOptionLabel}
          canSetPermissions={canSetPermissions}
          getOptions={getEntitySelectOptions(field)}
          onSelectChange={isPlanTypeSelect ? callback : null}
          isClearable={!field.multiple && !field.required && !isPlanTypeSelect}
          components={{
            MultiValueLabel,
            ...(searchTemplateIds && !field.multiple && { Control: OpenRiskControl })
          }}
        />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Tag) {
    return (
      <Grid key={field.id} onKeyPress={onKeyPress}>
        <CreatableSelect isDisabled={!canModify} field={field} validate={validate} required={field.required} />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.Address) {
    return (
      <Grid key={field.id}>
        <ReduxLocationPicker
          name={field.name}
          label={field.label}
          validate={validate}
          isDisabled={!canModify}
          required={field.required}
        />
      </Grid>
    )
  }

  if (field.fieldType === FieldType.DocumentOrLink) {
    return (
      <Grid key={field.id}>
        <DocumentOrLink field={field} name={field.name} required={field.required} isDisabled={!canModify} />
      </Grid>
    )
  }
}

export const getEntityStatus = (values: IFieldValueMapItem[] | string, fields: IField[]): string => {
  let statusValue = values

  if (Array.isArray(values)) {
    statusValue = pipe(
      find((item: IFieldValueMapItem) => item.fieldName === 'status'),
      (field) => {
        if (field) {
          return getOr(field.fieldValue, 'fieldValue.value', field)
        }
      }
    )(values)
  }

  if (statusValue) {
    return pipe(
      find((field: IField) => field.name === 'status'),
      getFp('options'),
      find((option) => option.id === statusValue),
      getFp('label')
    )(fields)
  }
}

export const normalizeFieldProperty = (fieldProperty) => {
  if (!isEmpty(fieldProperty) && !isNil(fieldProperty[0])) {
    return fieldProperty.map(({ label, value, id }) => ({
      id: id || uuid(),
      label,
      value
    }))
  }
  return []
}

export const getFieldSettings = (data: Record<string, any>, fieldType: FieldType) => {
  switch (fieldType) {
    case FieldType.RiskMatrix:
      return data.settings

    case FieldType.Date:
      return {
        format: getValueFromOption(data.settings.format),
        dateFormat: data.settings.dateFormat?.trim()
      }

    case FieldType.Number:
      return {
        minRange: data.settings?.minRange,
        maxRange: data.settings?.maxRange,
        manualSave: data.settings?.manualSave
      }

    case FieldType.Text:
      return {
        manualSave: data.settings?.manualSave
      }

    case FieldType.RecordLinks:
    case FieldType.ParentRelatedForms:
      return {
        // fieldIds is an array (Parent Related Forms) or a single option (RecordLinks) — castArray
        // normalizes either shape before mapping to the array the backend expects.
        fieldMappings: (
          (data.settings?.fieldMappings as Array<{ templateId: IOption; fieldIds: IOption[] | IOption }>) || []
        ).map((mapping) => ({
          riskTemplateId: getValueFromOption(mapping.templateId),
          fieldIds: castArray(mapping.fieldIds || []).map(getValueFromOption)
        }))
      }

    default:
      return {}
  }
}
